import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  addDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { Accessory } from '../../models/types';
import { db, storage } from '../config';

const COLLECTION_NAME = 'accessories';

// Create a new accessory
export const createAccessory = async (accessory: Omit<Accessory, 'id'>): Promise<void> => {
  const accessoriesCollection = collection(db, COLLECTION_NAME);
  const docRef = doc(accessoriesCollection);
  await setDoc(docRef, {
    ...accessory,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

// Get all accessories
export const getAccessories = async (): Promise<Accessory[]> => {
  const snapshot = await getDocs(collection(db, COLLECTION_NAME));
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : undefined,
      purchaseDate: data.purchaseDate && typeof data.purchaseDate.toDate === 'function'
        ? data.purchaseDate.toDate()
        : data.purchaseDate ? new Date(data.purchaseDate) : undefined,
      lastMaintenanceDate: data.lastMaintenanceDate && typeof data.lastMaintenanceDate.toDate === 'function'
        ? data.lastMaintenanceDate.toDate()
        : data.lastMaintenanceDate ? new Date(data.lastMaintenanceDate) : undefined,
      nextMaintenanceDate: data.nextMaintenanceDate && typeof data.nextMaintenanceDate.toDate === 'function'
        ? data.nextMaintenanceDate.toDate()
        : data.nextMaintenanceDate ? new Date(data.nextMaintenanceDate) : undefined,
    } as Accessory;
  });
};

// Get active accessories
export const getActiveAccessories = async (): Promise<Accessory[]> => {
  const q = query(collection(db, COLLECTION_NAME), where('status', '==', 'active'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : undefined,
      purchaseDate: data.purchaseDate && typeof data.purchaseDate.toDate === 'function'
        ? data.purchaseDate.toDate()
        : data.purchaseDate ? new Date(data.purchaseDate) : undefined,
      lastMaintenanceDate: data.lastMaintenanceDate && typeof data.lastMaintenanceDate.toDate === 'function'
        ? data.lastMaintenanceDate.toDate()
        : data.lastMaintenanceDate ? new Date(data.lastMaintenanceDate) : undefined,
      nextMaintenanceDate: data.nextMaintenanceDate && typeof data.nextMaintenanceDate.toDate === 'function'
        ? data.nextMaintenanceDate.toDate()
        : data.nextMaintenanceDate ? new Date(data.nextMaintenanceDate) : undefined,
    } as Accessory;
  });
};

// Get accessory by ID
export const getAccessoryById = async (id: string): Promise<Accessory | null> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : undefined,
      purchaseDate: data.purchaseDate && typeof data.purchaseDate.toDate === 'function'
        ? data.purchaseDate.toDate()
        : data.purchaseDate ? new Date(data.purchaseDate) : undefined,
      lastMaintenanceDate: data.lastMaintenanceDate && typeof data.lastMaintenanceDate.toDate === 'function'
        ? data.lastMaintenanceDate.toDate()
        : data.lastMaintenanceDate ? new Date(data.lastMaintenanceDate) : undefined,
      nextMaintenanceDate: data.nextMaintenanceDate && typeof data.nextMaintenanceDate.toDate === 'function'
        ? data.nextMaintenanceDate.toDate()
        : data.nextMaintenanceDate ? new Date(data.nextMaintenanceDate) : undefined,
    } as Accessory;
  }
  
  return null;
};

// Update accessory
export const updateAccessory = async (id: string, accessory: Partial<Accessory>): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...accessory,
    updatedAt: new Date(),
  });
};

// Update accessory status
export const updateAccessoryStatus = async (
  accessoryId: string,
  status: 'active' | 'inactive' | 'maintenance' | 'lost' | 'retired'
): Promise<void> => {
  try {
    const accessoryRef = doc(collection(db, COLLECTION_NAME), accessoryId);
    const now = new Date();
    
    await updateDoc(accessoryRef, {
      status,
      updatedAt: now
    });
  } catch (error) {
    console.error('Error updating accessory status:', error);
    throw error;
  }
};

// Delete accessory (sets status to retired)
export const deleteAccessory = async (accessoryId: string): Promise<void> => {
  try {
    await updateAccessoryStatus(accessoryId, 'retired');
  } catch (error) {
    console.error('Error deleting accessory:', error);
    throw error;
  }
};

// Upload accessory image
export const uploadAccessoryImage = async (file: File, accessoryId: string): Promise<string> => {
  try {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${accessoryId}_${Date.now()}.${fileExtension}`;
    const imageRef = ref(storage, `accessories/${accessoryId}/${fileName}`);
    
    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading accessory image:', error);
    throw error;
  }
};

// Get accessories by category
export const getAccessoriesByCategory = async (category: string): Promise<Accessory[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('category', '==', category));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Accessory[];
  } catch (error) {
    console.error(`Error getting accessories for category ${category}:`, error);
    throw error;
  }
};

// Get rentable accessories
export const getRentableAccessories = async (): Promise<Accessory[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('isRentable', '==', true),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Accessory[];
  } catch (error) {
    console.error('Error getting rentable accessories:', error);
    throw error;
  }
};

// Get accessories that need maintenance
export const getAccessoriesNeedingMaintenance = async (): Promise<Accessory[]> => {
  try {
    const accessories = await getAccessories();
    const now = new Date();
    
    return accessories.filter(accessory => {
      if (!accessory.nextMaintenanceDate) return false;
      return new Date(accessory.nextMaintenanceDate) <= now;
    });
  } catch (error) {
    console.error('Error getting accessories needing maintenance:', error);
    throw error;
  }
};

// Get accessories grouped by category
export const getAccessoriesByCategory_Grouped = async (): Promise<{ [key: string]: Accessory[] }> => {
  try {
    const accessories = await getAccessories();
    return accessories.reduce((acc, accessory) => {
      const category = accessory.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(accessory);
      return acc;
    }, {} as { [key: string]: Accessory[] });
  } catch (error) {
    console.error('Error getting accessories by category:', error);
    throw error;
  }
};

// Get low stock accessories (where availableQuantity is less than 20% of total quantity)
export const getLowStockAccessories = async (): Promise<Accessory[]> => {
  try {
    const accessories = await getActiveAccessories();
    return accessories.filter(accessory => {
      const threshold = Math.max(1, Math.floor(accessory.quantity * 0.2)); // 20% threshold, minimum 1
      return accessory.availableQuantity <= threshold;
    });
  } catch (error) {
    console.error('Error getting low stock accessories:', error);
    throw error;
  }
};

// Update accessory quantities (for rental/return operations)
export const updateAccessoryQuantities = async (
  accessoryId: string,
  availableQuantity: number,
  reservedQuantity?: number
): Promise<void> => {
  try {
    const updates: Partial<Accessory> = {
      availableQuantity,
      updatedAt: new Date()
    };
    
    if (reservedQuantity !== undefined) {
      updates.reservedQuantity = reservedQuantity;
    }
    
    await updateAccessory(accessoryId, updates);
  } catch (error) {
    console.error('Error updating accessory quantities:', error);
    throw error;
  }
};

// Get accessories by condition
export const getAccessoriesByCondition = async (condition: 'excellent' | 'good' | 'fair' | 'poor' | 'out-of-service'): Promise<Accessory[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('condition', '==', condition));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Accessory[];
  } catch (error) {
    console.error(`Error getting accessories with condition ${condition}:`, error);
    throw error;
  }
};

// Get accessories summary stats
export const getAccessoryStats = async (): Promise<{
  total: number;
  active: number;
  rentable: number;
  needingMaintenance: number;
  lowStock: number;
  byCategory: { [key: string]: number };
  byCondition: { [key: string]: number };
}> => {
  try {
    const accessories = await getAccessories();
    const now = new Date();
    
    const stats = {
      total: accessories.length,
      active: accessories.filter(a => a.status === 'active').length,
      rentable: accessories.filter(a => a.isRentable && a.status === 'active').length,
      needingMaintenance: accessories.filter(a => 
        a.nextMaintenanceDate && new Date(a.nextMaintenanceDate) <= now
      ).length,
      lowStock: accessories.filter(a => {
        const threshold = Math.max(1, Math.floor(a.quantity * 0.2));
        return a.availableQuantity <= threshold && a.status === 'active';
      }).length,
      byCategory: {} as { [key: string]: number },
      byCondition: {} as { [key: string]: number }
    };
    
    // Group by category
    accessories.forEach(accessory => {
      const category = accessory.category || 'other';
      stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
    });
    
    // Group by condition
    accessories.forEach(accessory => {
      const condition = accessory.condition;
      stats.byCondition[condition] = (stats.byCondition[condition] || 0) + 1;
    });
    
    return stats;
  } catch (error) {
    console.error('Error getting accessory stats:', error);
    throw error;
  }
}; 